<template>
	<div class="app-wrapper">
		<div class="app-container">
			<Header
				:naturally-visible="!isPairing && (!launchedApp || showOptionsMenu)"
				:is-menu-open="showOptionsMenu"
				@toggle-menu="toggleOptionsMenu"
				ref="headerRef"/>
	
			<LoadingScreen v-if="isLoading"
						   ref="loadingScreenRef"
						   @loading-complete="completeLoading"/>
			
			<Pairing v-else-if="isPairing"
					 @pairing-complete="handlePairingComplete"/>
	
			<HomeScreen v-else
						:apps="sortedApps"
						:active-app-id="activeAppId"
						:disabled="launchedApp !== null || showOptionsMenu"
						@set-active="setActiveApp"
						@launch-app="launchApp"
						@set-default-app="setDefaultApp"
						@ready="initHomeKeyboard"
						ref="homeScreenRef"/>
	
			<!-- App Viewer Component -->
			<AppViewer
				v-if="launchedApp"
				:app="launchedApp"
				:app-tile-element="appTileElement"
				:remote-id="launchedApp.remoteID || null"
				:bypass-thanks="bypassThanksScreen"
				v-model:showFullScreenTile="showFullScreenTile"
				@show-return-home-prompt="showReturnHomePrompt"
				@app-launch-failed="handleAppLaunchFailure"
				@game-ended="returnToHome(true)"
				ref="appViewerRef"
			/>
	
			<!-- Options Menu Component -->
			<OptionsMenu
				:show="showOptionsMenu"
				@close="handleOptionsMenuClose"
				@reboot="rebootSystem"
				@return-home="showReturnHomePrompt"
				:in-app="launchedApp !== null"
				ref="optionsMenuRef"
			/>
	
			<!-- Return Home Prompt Component -->
			<ReturnHomePrompt
				:show="showHomePrompt"
				:app-name="launchedApp ? launchedApp.name : ''"
				@confirm="returnToHome"
				@cancel="cancelReturnHome"
				ref="returnHomePromptRef"
			/>
	
			<!-- Set Default App Prompt Component -->
			<SetDefaultAppPrompt
				:show="showDefaultPrompt"
				:app-name="selectedDefaultApp ? selectedDefaultApp.name : ''"
				@confirm="confirmSetDefault"
				@cancel="cancelSetDefault"
				ref="setDefaultPromptRef"
			/>
	
			<NotificationContainer/>
		</div>
	</div>
</template>

<script setup>
import {ref, computed, onMounted, nextTick, watch, onUnmounted} from 'vue';
import LoadingScreen from './components/LoadingScreen.vue';
import HomeScreen from './components/HomeScreen.vue';
import AppViewer from './components/AppViewer.vue';
import OptionsMenu from './components/modals/OptionsMenu.vue';
import ReturnHomePrompt from './components/modals/ReturnHome.vue';
import SetDefaultAppPrompt from './components/modals/SetDefaultApp.vue';
import NotificationContainer from './components/notification/NotificationContainer.vue';
import {startupSequence} from './services/startup.js';
import {
    initKeyboardNavigation,
    cleanupKeyboardNavigation,
    handleGlobalShortcuts,
    setActiveContext
} from './services/keyboard.js';
import {apps, internalApps, fallbackApp, betaApps, initializeApps, initializeDefaultApp} from "@/apps.js";
import {systemInfo, params} from "@/systemInfo.js";
import {initRebootService, rebootDevice} from "@/services/reboot.js";
import Header from "@/components/Header.vue";
import {getLastState, setState} from "@/services/state.js";
import {showNotification} from "@/services/notification.js";
import {WIFI_OFF_ICON} from "@/services/helperFunctions.js";
import Pairing from "@/components/Pairing.vue";

// Default app name from environment variable
const DEFAULT_APP_NAME = import.meta.env.VITE_DEFAULT_APP_NAME || 'Trivia';
// Auto launch timeout from params (in seconds)
const AUTO_LAUNCH_TIMEOUT = params.autoLaunchTimeout || 30;

//obtain app ID based on app's name
const getAppId = (name) => {
// function getAppId(name) {
	if (!name) return undefined;
	
	const found = allApps.value.find(app => String(app.name).toLowerCase() === String(name).toLowerCase());
	return found ? found.id : undefined;
}

const allApps = computed(() => {
	return [...apps.value, ...betaApps.value, ...internalApps.value];
});

// Sort apps to ensure default app is first
const sortedApps = computed(() => {
    // Ensure we don't duplicate internal apps
    const uniqueApps = [...allApps.value];

    return uniqueApps.sort((a, b) => {
        // First sort by disabled status
        if (a.disabled && !b.disabled) return 1;
        if (!a.disabled && b.disabled) return -1;

        // Then sort by default status
        if (a.is_default) return -1;
        if (b.is_default) return 1;

        return 0;
    });
});

// State
const isLoading = ref(true);
const loadingStatus = ref('Initializing...');
const activeAppId = ref(null);
const loadingScreenRef = ref(null);
const homeScreenRef = ref(null);
const appViewerRef = ref(null);
const returnHomePromptRef = ref(null);
const setDefaultPromptRef = ref(null);
const optionsMenuRef = ref(null);
const headerRef = ref(null);
const launchedApp = ref(null);
const appTileElement = ref(null);
const showFullScreenTile = ref(true);
const returnToHomeInProgress = ref(false);
const autoLaunchTimer = ref(null);
const lastInteractionTime = ref(Date.now());
const bypassThanksScreen = ref(false);
const isPairing = ref(false)
let keyboardNav = null;

// Options menu state - simplified to a single boolean
const showOptionsMenu = ref(false);

// Return home prompt state
const showHomePrompt = ref(false);

// Set default app prompt state
const showDefaultPrompt = ref(false);
const selectedDefaultApp = ref(null);

// Set active app
const setActiveApp = (id) => {
    activeAppId.value = id;
    updateLastInteraction();
};

// Start auto launch timer
const startAutoLaunchTimer = () => {
    console.log(`Starting auto-launch timer with timeout: ${AUTO_LAUNCH_TIMEOUT} seconds`);

    // Clear any existing timer
    if (autoLaunchTimer.value) {
        clearInterval(autoLaunchTimer.value);
    }

    // Start a new timer
    autoLaunchTimer.value = setInterval(() => {
        // Check if it's time to auto-launch
        const idleTime = (Date.now() - lastInteractionTime.value) / 1000; // Convert to seconds

        // Update progress for the default app
        const defaultApp = allApps.value.find(app => app.is_default);
        if (defaultApp) {
            defaultApp.autoLaunchProgress = Math.min(1, idleTime / AUTO_LAUNCH_TIMEOUT);
        }

        // Auto-launch if idle time exceeds the timeout
        if (idleTime >= AUTO_LAUNCH_TIMEOUT && !launchedApp.value && !showOptionsMenu.value) {
            const defaultApp = allApps.value.find(app => app.is_default && !app.disabled);
            if (defaultApp) {
                launchApp(defaultApp.id);
                clearInterval(autoLaunchTimer.value);
            }
        }
    }, 1000); // Update every second
};

// Show set default app prompt
const showSetDefaultAppPrompt = (app) => {
    selectedDefaultApp.value = app;
    showDefaultPrompt.value = true;

    // Set active context to modal
    nextTick(() => {
        if (setDefaultPromptRef.value) {
            const buttons = setDefaultPromptRef.value.getButtons();
            setActiveContext('modal', {buttons});
        }
    });

    updateLastInteraction();
};

// Confirm setting default app
const confirmSetDefault = async () => {
    if (selectedDefaultApp.value) {
        // Animate modal closing
        if (setDefaultPromptRef.value) {
            await setDefaultPromptRef.value.animateModalClose();
        }

        setDefaultApp(selectedDefaultApp.value.id);
    }
    showDefaultPrompt.value = false;

    // Reset active context to home or app
    setActiveContext(launchedApp.value ? 'app' : 'home');

    updateLastInteraction();
};

// Cancel setting default app
const cancelSetDefault = async () => {
    // Animate modal closing
    if (setDefaultPromptRef.value) {
        await setDefaultPromptRef.value.animateModalClose();
    }

    showDefaultPrompt.value = false;

    // Reset active context to home or app
    setActiveContext(launchedApp.value ? 'app' : 'home');

    updateLastInteraction();
};

// Set default app
const setDefaultApp = (id) => {
    console.log("Setting default app ID:", id);

    // Update app is_default property for all apps
    allApps.value.forEach(app => {
        app.is_default = app.id === id;

        // Debug log
        if (app.id === id) {
            console.log(`Set app ${app.name} as default`);
        }
    });

    // Save to localStorage
    localStorage.setItem('defaultAppId', id.toString());

    // Update active app
    activeAppId.value = id;
    updateLastInteraction();
};

const toggleOptionsMenu = (forceState) => {
    const newState = forceState !== undefined ? forceState : !showOptionsMenu.value;

    if (newState === showOptionsMenu.value) return;

    showOptionsMenu.value = newState;
    updateLastInteraction();
};

// Handle options menu close event
const handleOptionsMenuClose = () => {
    showOptionsMenu.value = false;
    updateLastInteraction();
};

// Reboot system
const rebootSystem = () => {
    console.log('Rebooting system...');
    rebootDevice();
    updateLastInteraction();
};

// Show return home prompt
const showReturnHomePrompt = () => {
    if (!launchedApp.value) return;

    showHomePrompt.value = true;

    // Set active context to modal
    nextTick(() => {
        if (returnHomePromptRef.value) {
            const buttons = returnHomePromptRef.value.getButtons();
            setActiveContext('modal', {buttons});
        }
    });

    updateLastInteraction();
};

// Cancel return home
const cancelReturnHome = async () => {
    // Animate modal closing
    if (returnHomePromptRef.value) {
        await returnHomePromptRef.value.animateModalClose();
    }

    showHomePrompt.value = false;

    // Reset active context to app
    setActiveContext('app');

    updateLastInteraction();
};

// Launch app
const launchApp = async (evt, remote=null) => {
	if (!navigator.onLine) {
		showNotification(`Can't launch app<br>No internet connection`, WIFI_OFF_ICON, false, true);
		return;
	}

	setState('busy')
	const appKey = remote ? (evt.detail ?? evt)?.appKey : evt;
	const params = remote ? (evt.detail ?? evt)?.params ?? {} : {};
	
	console.log(`[LaunchApp] ID: ${appKey}  REMOTE: ${remote} ID: ${params.id}`);
	
	const id = remote ? await getAppId(appKey) : appKey;
	
    // Find the app in all apps
    const app = allApps.value.find(app => app.id === id);

    if (!app || app.disabled === true) {
        console.log(`[LaunchApp] App ${app?.name || id} is disabled, cannot launch`);
        setState(launchedApp.value?.name ?? 'home')
        return;
    }
	
	if (remote && params.ids) {
		app.remoteID = params.ids // array of game IDs
	} else if (remote && params.id) {
		app.remoteID = `${params.id}` // single game ID string
	}
	
    // Get the app tile element from HomeScreen - always get the current position
    if (homeScreenRef.value) {
        try {
            appTileElement.value = await homeScreenRef.value.getAppTileElement(id);
            console.log("Got app tile element for launch:");
        } catch (error) {
            console.error("Error getting app tile element:", error);
            appTileElement.value = null;
        }
    }
	
	if (appTileElement.value) {
		appTileElement.value.scrollIntoView({
			behavior: 'auto',
			block: 'nearest',
			inline: 'nearest'
		});
	}
	await new Promise(resolve => setTimeout(resolve, 500));
	
    // Set the launched app
    launchedApp.value = app;
	setState(app.name)
    // Set active context to app
    setActiveContext('app');

    updateLastInteraction();
};

// Return to home screen
const returnToHome = async (gameEnded = false, bypassThanks = false) => {
    if (!launchedApp.value) {
		console.log("NO LAUNCHED APP. SKIPPING RETURN TO HOME")
		window.dispatchEvent(new CustomEvent('remote-returned-home', {detail: {response: true}}));
		setState('home')
		return;
	}
	
	if (returnToHomeInProgress.value && appViewerRef.value?.isShowingThanks) {
        returnToHomeInProgress.value = false; // Force reset the in-progress flag to allow a new return attempt
    }

    if (returnToHomeInProgress.value) return; // exit if return is still in progress after check

    returnToHomeInProgress.value = true;

	setState('busy')
	console.log("RETURNING HOME")
	bypassThanksScreen.value = bypassThanks

    if (returnHomePromptRef.value) {
        await returnHomePromptRef.value.animateModalClose();
    }

    showHomePrompt.value = false;
    updateLastInteraction();
    let animationSucceeded = false;

    try {
        if (!gameEnded) {
          const targetAppTile = await homeScreenRef?.value.getAppTileElement(launchedApp.value.id);
          console.log("Got app tile element for return:");

          if (appViewerRef.value && targetAppTile) {
            animationSucceeded = await appViewerRef?.value.animateReturnToHome(targetAppTile);
          }
        }
    } catch (error) {
        console.error("Error getting app tile element for return:", error);
    } finally {
		console.log(`LAUNCHED APP: ${launchedApp.value}`)
		
		launchedApp.value = null;
        returnToHomeInProgress.value = false;

        setActiveContext('home');
		setState('home')
		bypassThanksScreen.value = false
		
		window.dispatchEvent(new CustomEvent('remote-returned-home', {detail: {response: true}}));
        startAutoLaunchTimer();
    }
};

const handleAppLaunchFailure = (error) => {
    const appName = launchedApp.value?.name || 'app';
    const offline = !navigator.onLine;
    const detail = offline ? 'No internet connection' : (typeof error === 'string' ? error : error?.message || '');

    showNotification(
        `Unable to launch <strong>${appName}</strong>${detail ? `<br>${detail}` : ''}`,
        offline ? WIFI_OFF_ICON : '',
        false,
        true
    );
    
    returnToHome();
};

const handlePairingComplete = async (handshakeData) => {
	console.log("Pairing complete! Resuming startup sequence...");
	// Token is saved in localStorage by Pairing.vue.
	// Re-running startup will now detect the token, process the virtual-paired
	// logic, fetch apps, connect to remote, and transition to HomeScreen.
	await runStartup();
};

const completeLoading = () => {
    isLoading.value = false;

    console.log("OPTIONS MENU VALUE", showOptionsMenu.value)
    // Close options menu if it's open
    if (showOptionsMenu.value) {
        toggleOptionsMenu(false);
    }

    // Set default app as active
    nextTick(() => {
        const defaultApp = allApps.value.find(app => app.is_default);
        if (defaultApp) {
            activeAppId.value = defaultApp.id;
        }

        // Set active context to home
        setActiveContext('home');
		setState('home')
		window.dispatchEvent(new CustomEvent('remote-welcome', {detail: {response: true}}));
		
        // Start the auto-launch timer
        startAutoLaunchTimer();
    });
};

const initHomeKeyboard = () => {
    keyboardNav = initKeyboardNavigation(
        sortedApps.value,
        setActiveApp,
        launchApp,
        (element) => {
            if (element) {
                element.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            }
        },
        setDefaultApp,
        showSetDefaultAppPrompt,
        toggleOptionsMenu
    );
};

// Handle keyboard shortcuts
const handleKeyboardShortcuts = (event) => {
    // Only handle if not in an input field
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
    }

    updateLastInteraction();

    // Use the global shortcuts handler from keyboard.js
    handleGlobalShortcuts(event, {
        showOptionsMenu: showOptionsMenu.value,
        toggleOptionsMenu,
        closeOptionsMenu: () => toggleOptionsMenu(false),
        showHomePrompt: showHomePrompt.value,
        cancelReturnHome,
        returnToHome,
        showDefaultPrompt: showDefaultPrompt.value,
        cancelSetDefault,
        confirmSetDefault,
        rebootSystem,
        showReturnHomePrompt,
        launchedApp: launchedApp.value,
        activeAppId: activeAppId.value,
        launchApp,
        allApps: allApps.value,
        showSetDefaultAppPrompt,
    });
};

const handleGlobalHover = (event) => {
	const button = event.target.closest('button, .modal-button, [role="button"]');
	
	if (button) {
		document.querySelectorAll('.keyboard-focused').forEach((el) => {
			if (el !== button) {
				el.classList.remove('keyboard-focused');
				el.blur()
			}
		});
		button.classList.add('keyboard-focused');
		button.focus()
	} else {
		document.querySelectorAll('.keyboard-focused').forEach((el) => {
			el.classList.remove('keyboard-focused');
			el.blur()
		});
	}
};

// Mouse movement handler
const handleMouseMove = () => {
	resetCursorTimer()
    updateLastInteraction();
};

// Update last interaction time
const updateLastInteraction = () => {
	lastInteractionTime.value = Date.now();
};

let cursorTimeout;

function resetCursorTimer() {
	document.documentElement.classList.remove('hide-cursor');
	clearTimeout(cursorTimeout);
	cursorTimeout = setTimeout(() => {
		document.documentElement.classList.add('hide-cursor');
	}, 3000);
}

// Function to handle app restart requests
const handleAppRestart = async (event) => {
    const {appId, appName} = event.detail;

    console.log(`Handling restart for app ${appName} (${appId})`);

    // Find the app object
    const appToRestart = allApps.value.find(a => a.id === appId);
    if (!appToRestart) {
        console.error(`Could not find app with ID ${appId} to restart`);
        return;
    }

    // If the app is not currently launched, just launch it directly
    if (!launchedApp.value || launchedApp.value.id !== appId) {
        launchApp(appId);
        return;
    }

    // Set a flag to prevent multiple restart attempts
    const restartInProgress = ref(true);

    try {
        // Find the app tile element - this will be our target for animation
        let targetAppTile = null;
        if (homeScreenRef.value) {
            targetAppTile = await homeScreenRef.value.getAppTileElement(appId);
        }

        // If we have an app viewer, use it to animate return home
        if (appViewerRef.value) {
            // Return to home without showing the prompt
            await appViewerRef.value.animateReturnToHome(targetAppTile);
        }

        // Reset the launched app
        launchedApp.value = null;

        // Wait a short delay before relaunching to ensure clean state
        await new Promise(resolve => setTimeout(resolve, 300));

        // Launch the app again
        launchApp(appId);
    } catch (error) {
        console.error(`Error during app restart: ${error.message}`);
        // Fallback: force close and relaunch
        launchedApp.value = null;
        setActiveContext('home');

        // Small delay before relaunch
        setTimeout(() => {
            launchApp(appId);
        }, 500);
    } finally {
        restartInProgress.value = false;
    }
};

const runStartup = async () => {
	isLoading.value = true;
	isPairing.value = false;
	
	try {
		const result = await startupSequence(updateStatus);
		
		// Halt and transition to pairing screen if required
		if (result && result.status === 'pairing_required') {
			isLoading.value = false;
			isPairing.value = true;
			return;
		}
		
		// Otherwise, startup succeeded. Trigger LoadingScreen exit animation.
		setTimeout(() => {
			if (loadingScreenRef.value) {
				loadingScreenRef.value.completeAnimation();
			} else {
				completeLoading(); // Fallback if ref is missing
			}
		}, 1000);
	} catch (error) {
		console.error('Startup sequence failed:', error);
		loadingStatus.value = 'Error during startup. Please restart the application.';
	}
};

// Update loading status during startup sequence
const updateStatus = (status) => {
	loadingStatus.value = status;
};

// Initialize everything on component mount
onMounted(async () => {
	setState('loading')
	window.addEventListener('resize', () => {
		systemInfo.screen = `${window.innerWidth}x${window.innerHeight}`
	});

	//Buzztime remote app listeners
	window.addEventListener('remote-launch-app', (e) => launchApp(e, true))
	window.addEventListener('remote-return-home', (e) => returnToHome(false, e.detail?.switchingApps ?? false))
	window.addEventListener('remote-toggle-menu', () => toggleOptionsMenu())

    initHomeKeyboard();

	document.addEventListener('mouseover', handleGlobalHover);
    document.addEventListener('keydown', handleKeyboardShortcuts);// keyboard event listeners for shortcuts
    document.addEventListener('mousemove', handleMouseMove); // mouse movement listener
    window.addEventListener('app-restart-requested', handleAppRestart); // specific app restart listener

	runStartup()
    initRebootService();
});

onUnmounted(() => {
    // Clean up keyboard navigation
    cleanupKeyboardNavigation();
	
	window.removeEventListener('remote-launch-app', (e) => launchApp(e, true))
	
	document.removeEventListener('mouseover', handleGlobalHover);
    document.removeEventListener('keydown', handleKeyboardShortcuts);
    document.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('app-restart-requested', handleAppRestart);
	clearTimeout(cursorTimeout);

    // Clear auto-launch timer
    if (autoLaunchTimer.value) {
        clearInterval(autoLaunchTimer.value);
    }
});

defineExpose({
	setState
});

// Watch for changes in UI state to update keyboard context
watch([showOptionsMenu, showHomePrompt, showDefaultPrompt, launchedApp], () => {
    if (keyboardNav) {
        keyboardNav.updateContext({
            showOptionsMenu: showOptionsMenu.value,
            showHomePrompt: showHomePrompt.value,
            showDefaultPrompt: showDefaultPrompt.value,
            launchedApp: launchedApp.value
        });
    }
}, {flush: 'sync'});
</script>

<style>
@import '@/assets/styles/global.css';
@import '@/assets/styles/keyboard.css';

/* Base styles */
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    /* cursor: none; !* TODO: Uncomment these before deploy?? *! */
    /* pointer-events: none; */
}

body {
    font-family: var(--font-family-primary);
    background-color: var(--color-background);
    color: var(--color-text);
    overflow: hidden;
    user-select: none;
}

.app-container {
    width: calc(var(--cqw) * 100);
    height: calc(var(--cqh) * 100);
    overflow: hidden;
    overflow: clip;
    position: relative;
}

/* Mouse disabled state */
body.mouse-disabled {
    /*cursor: none;*/
}
</style>
